import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon } from 'lucide-react-native';
import { useRef, useEffect, useState, useMemo } from 'react';
import { twJoin } from 'tailwind-merge';

import { MultiSelectActionsheet } from '@/components/MultiSelectActionsheet';
import { ProfileSheet } from '@/components/ProfileSheet';
import { ExpertMetrics } from '@/components/profile/ExpertMetrics';
import { ActionsheetItem } from '@/components/ui/actionsheet';
import { Avatar, AvatarFallbackText, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonText } from '@/components/ui/button';
import {
  Checkbox,
  CheckboxGroup,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from '@/components/ui/checkbox';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text as LinkText, Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { EXPERIMENTS } from '@/constants/experiments';
import { useExpertGrouping } from '@/hooks/useExpertGrouping';
import { useMultiSelect } from '@/hooks/useMultiSelect';
import { getResponseTimeSettingsV2QueryOptions } from '@/services/getResponseTimeSettingsV2';
import { segmentClient } from '@/util/analytics';
import { formatResponseTimeLabel } from '@/util/formatResponseTime';
import { getNativeTextProps } from '@/util/getNativeTextProps';
import { getUserName } from '@/util/getUserName';
import type { ExpertProfile } from '@/util/mapDirectoryProfiles';
import { isSurgeon } from '@/utils/expertHelpers';

interface Props {
  value: number[];
  options: ExpertProfile[];
  onChange: (val: number[]) => void;
  placeholder?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  currentUser?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  maxItems?: number;
  onClose: (value: number[]) => void;
  isOpen?: boolean;
  onSelectedItemPress?: () => void;
  cancerType?: string;
}

type ExpertItemProps = {
  expert: ExpertProfile;
  isHighlighted?: boolean;
  isItemDisabled: (expert: ExpertProfile) => boolean;
  handleViewProfile: (expertId: string) => void;
  responseTimeBuckets: { label: string; maxTime: number }[];
};

function ExpertItem({
  expert,
  isHighlighted = false,
  isItemDisabled,
  handleViewProfile,
  responseTimeBuckets,
}: ExpertItemProps) {
  return (
    <VStack className="mb-3 last:mb-0">
      <ActionsheetItem
        className={twJoin(
          'rounded border border-outline-200 p-0',
          isHighlighted
            ? 'bg-tertiary-50 data-[hover=false]:bg-tertiary-50 data-[active=true]:bg-tertiary-50'
            : 'data-[hover=false]:bg-transparent data-[active=true]:bg-transparent'
        )}>
        <HStack className="w-full items-center justify-between gap-3 px-4 py-3">
          <Checkbox
            value={String(expert.id)}
            size="md"
            aria-label={expert.name}
            testID={`expert-checkbox-${expert.id}`}
            className="flex-1 flex-row items-center px-0 py-0 focus:bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-background-100"
            isDisabled={isItemDisabled(expert)}>
            <CheckboxIndicator>
              <CheckboxIcon as={CheckIcon} />
            </CheckboxIndicator>
            <HStack className="ml-0.5 flex-1 items-center gap-1.5 web:ml-2 web:gap-3">
              <Avatar size="md" className="shrink-0">
                <AvatarFallbackText>{expert.nameWithoutDoctor}</AvatarFallbackText>
                <AvatarImage source={expert.avatarSrc ? { uri: expert.avatarSrc } : undefined} />
              </Avatar>
              <VStack className="min-w-0 flex-1">
                <HStack className="mb-1 flex-wrap items-center gap-2 web:flex-row web:gap-1.5">
                  <CheckboxLabel
                    className="text-left font-medium text-sm text-typography-800"
                    {...getNativeTextProps()}>
                    {expert.name}
                  </CheckboxLabel>
                  {expert.speciality && (
                    <Badge
                      size="sm"
                      action={isHighlighted ? 'muted' : 'info'}
                      className={twJoin(
                        'hidden self-start px-2 web:flex',
                        isHighlighted && 'bg-tertiary-100'
                      )}
                      accessibilityLabel={isHighlighted ? 'Surgeon' : undefined}>
                      <BadgeText
                        className={twJoin(
                          'text-xs',
                          isHighlighted ? 'text-tertiary-800' : 'text-info-800'
                        )}>
                        {expert.speciality}
                      </BadgeText>
                    </Badge>
                  )}
                </HStack>
                {expert.speciality && (
                  <Badge
                    size="sm"
                    action={isHighlighted ? 'muted' : 'info'}
                    className={twJoin(
                      'mb-1 self-start px-2 web:hidden',
                      isHighlighted && 'bg-tertiary-100'
                    )}
                    accessibilityLabel={isHighlighted ? 'Surgeon' : undefined}>
                    <BadgeText
                      className={twJoin(
                        'text-xs',
                        isHighlighted ? 'text-tertiary-800' : 'text-info-800'
                      )}>
                      {expert.speciality}
                    </BadgeText>
                  </Badge>
                )}
                {expert.title && (
                  <Text
                    size="2xs"
                    className="mb-1 text-typography-700 web:text-xs"
                    {...getNativeTextProps()}>
                    {expert.title}
                  </Text>
                )}
                {expert.institution && (
                  <Text
                    size="2xs"
                    className="mb-1 text-typography-700 web:text-xs"
                    {...getNativeTextProps()}>
                    {expert.institution}
                  </Text>
                )}
                {/*
                 * Both metrics share the line rather than stacking. Rows here are already
                 * ragged, since only some experts carry a response time, and giving the badge
                 * its own line would add a fifth row height for the handful of experts who
                 * hold it.
                 *
                 * Inert on this surface. The whole row is a checkbox target, so a pressable
                 * label would either steal the tap or be swallowed by it, and the popover it
                 * opens would have to layer over the actionsheet it lives in.
                 */}
                <ExpertMetrics
                  expertId={expert.id}
                  responseTimeLabel={
                    expert.responseTime
                      ? formatResponseTimeLabel(expert.responseTime, responseTimeBuckets)
                      : undefined
                  }
                />
              </VStack>
            </HStack>
          </Checkbox>

          <Pressable
            onPress={() => {
              handleViewProfile(String(expert.id));
            }}
            className="hidden shrink-0 web:flex">
            <LinkText className="text-right font-normal text-typography-900 underline" size="xs">
              View Profile
            </LinkText>
          </Pressable>
        </HStack>
      </ActionsheetItem>
    </VStack>
  );
}

/**
 * Multi-Select Component for Expert Selection
 *
 * @overview
 * A reusable multi-select component for selecting medical experts. Features a compact
 * trigger with selected expert badges and an actionsheet modal for selection with
 * rich expert information display.
 *
 * @behavior
 * - Maximum 3 experts can be selected (enforced via disabled state)
 * - Selected experts shown as selectable badges with avatars and titles
 * - Actionsheet provides checkbox selection with expert details
 * - Expert edits happen through the selection sheet
 * - Supports loading and disabled states
 */
export function ExpertMultiSelect({
  value,
  options,
  onChange: onChangeProp,
  placeholder = 'Select up to 2 additional experts',
  isLoading = false,
  isDisabled = false,
  currentUser,
  maxItems = 2,
  onClose,
  isOpen: controlledIsOpen = false,
  onSelectedItemPress,
  cancerType,
}: Props) {
  const userName = getUserName(currentUser);

  const { variant, shouldGroupSurgicalOncologists, showMultiDisciplinarySuggestion, isApplicable } =
    useExpertGrouping(cancerType);

  const { surgicalOncologists, others } = useMemo(() => {
    if (!shouldGroupSurgicalOncologists) {
      return { surgicalOncologists: [], others: options };
    }

    return {
      surgicalOncologists: options.filter((e) => isSurgeon(e.speciality)),
      others: options.filter((e) => !isSurgeon(e.speciality)),
    };
  }, [options, shouldGroupSurgicalOncologists]);

  const onChange = (expertIds: number[]) => {
    onChangeProp(expertIds);
  };

  const {
    isOpen,
    setIsOpen,
    selectedOptions,
    handleSelectionChange: originalHandleSelectionChange,
    isItemDisabled,
  } = useMultiSelect({
    value,
    options,
    onChange,
    maxItems,
    getId: (expert) => Number(expert.id),
  });

  const handleSelectionChange = (vals: string[]) => {
    const newExpertIds = vals.map(Number);

    const currentIds = new Set(value || []);

    const added = newExpertIds.filter((id) => !currentIds.has(id));

    if (added.length > 0) {
      const expert = options.find((e) => Number(e.id) === added[0]);

      if (expert) {
        segmentClient.track(ANALYTICS_EVENTS.EXPERT_SELECTED, {
          expertId: added[0],
          expertName: expert.name,
          expertSpeciality: expert.speciality || '',
          selectedCount: newExpertIds.length,
          addedCount: added.length,
          userName,
          surgeonGroupingVariant: isApplicable ? variant : 'not_applicable',
          isSurgeon: isSurgeon(expert.speciality),
          cancerType: cancerType || '',
        });
      }
    }

    originalHandleSelectionChange(vals);
  };

  const [profileSheetOpen, setProfileSheetOpen] = useState(false);

  const [selectedExpertId, setSelectedExpertId] = useState<string | null>(null);

  const [wasExpertSelectOpen, setWasExpertSelectOpen] = useState(false);

  const isOpeningProfileSheetRef = useRef(false);

  useEffect(() => {
    setIsOpen(controlledIsOpen);
  }, [controlledIsOpen, setIsOpen]);

  const hasTrackedExperimentRef = useRef(false);

  useEffect(() => {
    if (isOpen && !hasTrackedExperimentRef.current) {
      hasTrackedExperimentRef.current = true;

      const surgicalOncologistCount = options.filter((e) => isSurgeon(e.speciality)).length;

      const multiDisciplinarySuggestionDisplayed =
        showMultiDisciplinarySuggestion &&
        shouldGroupSurgicalOncologists &&
        surgicalOncologistCount > 0;

      segmentClient.track(ANALYTICS_EVENTS.EXPERIMENT_VIEWED, {
        experimentName: EXPERIMENTS.SURGEON_GROUPING,
        variant,
        isApplicable,
        surgicalOncologistsGrouped: shouldGroupSurgicalOncologists,
        multiDisciplinarySuggestionDisplayed,
        cancerType: cancerType || '',
        surgicalOncologistCount,
        totalExpertCount: options.length,
        userName,
      });
    }
  }, [
    isOpen,
    variant,
    isApplicable,
    shouldGroupSurgicalOncologists,
    showMultiDisciplinarySuggestion,
    cancerType,
    options,
    userName,
  ]);

  const handleClose = () => {
    if (isOpeningProfileSheetRef.current) {
      isOpeningProfileSheetRef.current = false;

      return;
    }

    if (value.length > 0) {
      segmentClient.track(ANALYTICS_EVENTS.ADDITIONAL_EXPERTS_SELECTED, {
        selectedCount: value.length,
        selectedExperts: selectedOptions.map((expert) => ({
          id: expert.id,
          name: expert.name,
          speciality: expert.speciality,
        })),
        userName,
      });
    }

    setIsOpen(false);

    onClose(value);
  };

  const handleViewProfile = (expertId: string) => {
    const expert = options.find((e) => e.id === expertId);

    segmentClient.track(ANALYTICS_EVENTS.EXPERT_PROFILE_VIEWED_FROM_SELECTION, {
      expertId,
      expertName: expert?.name || '',
      expertSpeciality: expert?.speciality || '',
      userName,
    });

    setWasExpertSelectOpen(isOpen);

    isOpeningProfileSheetRef.current = true;

    setIsOpen(false);

    setSelectedExpertId(expertId);

    setProfileSheetOpen(true);
  };

  const handleProfileSheetClose = () => {
    setProfileSheetOpen(false);

    setSelectedExpertId(null);
  };

  const handleReopenExpertSelect = () => {
    if (wasExpertSelectOpen) {
      setIsOpen(true);

      setWasExpertSelectOpen(false);
    }
  };

  const handleSelectedPress = () => {
    if (!isDisabled) {
      if (onSelectedItemPress) {
        onSelectedItemPress();
      } else {
        segmentClient.track(ANALYTICS_EVENTS.EXPERT_SELECTION_MODAL_OPENED, {
          selectedCount: (value || []).length,
          availableCount: options.length,
          userName,
        });

        setIsOpen(true);
      }
    }
  };

  const { data: responseTimeBuckets = [] } = useQuery(getResponseTimeSettingsV2QueryOptions());

  if (isDisabled) {
    return (
      <VStack className="min-h-9 items-center justify-center rounded border border-outline-300 bg-background-50 px-3 py-2 opacity-50">
        <Text className="text-sm text-typography-400">{placeholder}</Text>
      </VStack>
    );
  }

  return (
    <>
      {/* Trigger */}
      {selectedOptions.length > 0 ? (
        <VStack className={twJoin('gap-2', 'web:gap-3')}>
          {selectedOptions.map((expert) => {
            const highlighted = isSurgeon(expert.speciality);

            return (
              <Pressable
                key={expert.id}
                onPress={() => handleViewProfile(String(expert.id))}
                accessibilityLabel={
                  highlighted
                    ? `${expert.name}, surgical oncology expert`
                    : `${expert.name}, view profile`
                }
                className={twJoin(
                  'flex-row items-start gap-2 rounded-lg border px-2.5 py-2.5',
                  'web:gap-3 web:rounded-xl web:px-3 web:py-3',
                  highlighted
                    ? 'border-outline-200 bg-tertiary-50'
                    : 'border-outline-100 bg-background-0'
                )}>
                <Avatar size={isWeb ? 'md' : 'sm'} className="shrink-0">
                  <AvatarFallbackText>{expert.nameWithoutDoctor || expert.name}</AvatarFallbackText>
                  <AvatarImage source={expert.avatarSrc ? { uri: expert.avatarSrc } : undefined} />
                </Avatar>
                <VStack className="min-w-0 flex-1 gap-0.5">
                  <HStack className="flex-wrap items-center gap-1.5">
                    <Text size={isWeb ? 'sm' : 'xs'} className="font-medium text-typography-900">
                      {expert.name}
                    </Text>
                    {expert.speciality ? (
                      <Badge
                        size={isWeb ? 'sm' : 'md'}
                        action={highlighted ? 'muted' : 'info'}
                        className={twJoin(
                          'px-1.5 py-0',
                          'web:px-2 web:py-0.5',
                          highlighted && 'bg-tertiary-100'
                        )}>
                        <BadgeText
                          className={twJoin(
                            'text-xs',
                            highlighted ? 'text-tertiary-800' : 'text-info-800'
                          )}>
                          {expert.speciality}
                        </BadgeText>
                      </Badge>
                    ) : null}
                  </HStack>
                  <ExpertMetrics
                    expertId={expert.id}
                    responseTimeLabel={
                      expert.responseTime
                        ? formatResponseTimeLabel(expert.responseTime, responseTimeBuckets)
                        : undefined
                    }
                  />

                  <Text
                    size="xs"
                    className="hidden font-normal text-typography-900 underline web:flex">
                    View Profile
                  </Text>
                </VStack>
              </Pressable>
            );
          })}

          <Button
            variant="link"
            size="lg"
            action="primary"
            onPress={handleSelectedPress}
            className={twJoin('h-auto min-h-9 justify-start self-start px-0 py-1', 'web:min-h-11')}
            accessibilityLabel="Edit experts">
            <ButtonText className="font-normal text-sm text-typography-800 underline web:text-base data-[hover=true]:web:no-underline">
              Edit experts
            </ButtonText>
          </Button>
        </VStack>
      ) : (
        <Pressable
          onPress={handleSelectedPress}
          disabled={isDisabled}
          className="min-h-9 flex-row flex-wrap items-center rounded border border-outline-300 bg-background-0 px-3 py-2">
          <Text className="text-sm text-typography-400">{placeholder}</Text>
        </Pressable>
      )}

      {/* Actionsheet */}
      <MultiSelectActionsheet isOpen={isOpen} onClose={handleClose} title={placeholder}>
        <CheckboxGroup
          value={value.map(String)}
          onChange={handleSelectionChange}
          aria-label={placeholder}>
          {shouldGroupSurgicalOncologists && surgicalOncologists.length > 0 && (
            <>
              <Text
                className="mb-2 px-1 font-semibold text-sm text-typography-600"
                role="heading"
                aria-level={3}
                aria-label="Surgical Oncology Experts section">
                Surgical Oncology Experts
              </Text>
              {surgicalOncologists.map((expert) => (
                <ExpertItem
                  key={expert.id}
                  expert={expert}
                  isHighlighted={isSurgeon(expert.speciality)}
                  isItemDisabled={isItemDisabled}
                  handleViewProfile={handleViewProfile}
                  responseTimeBuckets={responseTimeBuckets}
                />
              ))}
              {showMultiDisciplinarySuggestion && (
                <Text className="mt-2 px-1 text-xs italic text-typography-400">
                  Consider adding a surgical oncologist if this inquiry would benefit from a
                  multi-disciplinary review.
                </Text>
              )}
              {others.length > 0 && (
                <Text
                  className="mb-2 mt-4 px-1 font-semibold text-sm text-typography-600"
                  role="heading"
                  aria-level={3}
                  aria-label="Other Experts section">
                  Other Experts
                </Text>
              )}
            </>
          )}
          {(shouldGroupSurgicalOncologists ? others : options).map((expert) => (
            <ExpertItem
              key={expert.id}
              expert={expert}
              isHighlighted={isSurgeon(expert.speciality)}
              isItemDisabled={isItemDisabled}
              handleViewProfile={handleViewProfile}
              responseTimeBuckets={responseTimeBuckets}
            />
          ))}
        </CheckboxGroup>
      </MultiSelectActionsheet>

      {/* Profile Sheet for Mobile */}
      {selectedExpertId && (
        <ProfileSheet
          isOpen={profileSheetOpen}
          onClose={handleProfileSheetClose}
          expertId={selectedExpertId}
          onReopenExpertSelect={handleReopenExpertSelect}
        />
      )}
    </>
  );
}
